import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonItem, IonModal, IonSelect, IonSelectOption, IonTitle, IonToolbar } from "@ionic/react"
import { Employee } from "../../../models/companyModels";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { ChangeEvent, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../data/redux/hooks";
import { connectTraineeAndTrainer, } from "../../../data/redux/companySlice";
import { addCircleOutline, removeCircleOutline } from "ionicons/icons";

interface ConnectTraineeWithTrainerModalProps {
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void,
    selectedTrainee?: Employee,
    trainers?: Employee[],
    company_admins?: Employee[]
}

interface ConnectTrainerWithTraineeFormData {
    trainer_id: number | string,
    trainee_id?: number,
    additional_trainers: { trainerId: number | undefined }[],
    reload?: boolean,
}

function trainerIdsToString(array: { trainerId: number }[]): string {
    return array
        .filter(item => typeof item.trainerId === 'number' && !isNaN(item.trainerId))
        .map(item => item.trainerId)
        .join(';');
}

const ConnectTraineeWithTrainerModal: React.FC<ConnectTraineeWithTrainerModalProps> = ({ isOpen, setIsOpen, selectedTrainee, trainers, company_admins }) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.user)

    // Combine trainers and company_admins into one array
    const allTrainers = [...(trainers || []), ...(company_admins || [])];

    const { control, handleSubmit, reset, watch } = useForm<ConnectTrainerWithTraineeFormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            trainer_id: "",
            additional_trainers: []
        },
    });
    const { fields, append, remove } = useFieldArray({
        name: "additional_trainers",
        control,
    });

    const watchAdditionalTrainers = watch("additional_trainers", []);
    const watchTrainerId = watch("trainer_id");

    // Get the IDs of trainers already selected
    const selectedTrainerIds = [
        watchTrainerId, // Include the main trainer_id
        ...watchAdditionalTrainers.map((trainer) => trainer.trainerId), // Include additional trainers
    ].filter((id) => id !== undefined && id !== ""); // Remove undefined value


    const onSubmit: SubmitHandler<ConnectTrainerWithTraineeFormData> = (data: ConnectTrainerWithTraineeFormData) => {
        let additional_trainers = "";
        if (data.additional_trainers.length > 0) {
            additional_trainers = trainerIdsToString(data.additional_trainers);
        }

        let reqData: any = {
            trainer_id: data.trainer_id,
            trainee_id: selectedTrainee!.id,
            additional_trainers: additional_trainers
        }
        // TODO: reload muss true sein, wenn additional trainers user id enthält
        if (user &&( (user.id === selectedTrainee?.trainers?.[0]?.id || user.id === data.trainer_id) || (selectedTrainee.additional_trainers.find(trainer => trainer.id === user.id) || additional_trainers.includes(user.id.toString())))) {
            reqData.reload = true
        } else {
            reqData.reload = false
        }
        dispatch(connectTraineeAndTrainer(reqData))

        setIsOpen(false)
    }

    const onCancel = () => {
        reset()
        setIsOpen(false)
    }


    useEffect(() => {
        if (selectedTrainee && isOpen) {
            reset({
                trainer_id: (isOpen && selectedTrainee && selectedTrainee.trainers && selectedTrainee.trainers[0]) ? selectedTrainee.trainers[0].id : "",
                additional_trainers: selectedTrainee?.additional_trainers?.map(trainer => ({
                    trainerId: trainer.id,
                })) || [],
            })
        }

    }, [selectedTrainee, isOpen, reset])

    return (
        <IonModal isOpen={isOpen}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>
                        Azubi einem Ausbilder zuweisen
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <p>Hier können Sie festlegen, welcher Ausbilder Ihren Azubi <b>{selectedTrainee?.first_name} {selectedTrainee?.name}</b> hauptsächlich betreut.</p>
                    <IonItem className="registerInput">
                        {(trainers && company_admins) &&
                            <Controller
                                name="trainer_id"
                                control={control}
                                render={({ field }) => <IonSelect
                                    interfaceOptions={{ className: "connectTraineeWithTrainerSelect" }}
                                    label="Ausbilder"
                                    labelPlacement="floating"
                                    cancelText="Abbrechen"
                                    onIonChange={(e: { detail: { value: string | number | ChangeEvent<Element>; }; }) => field.onChange(e.detail.value)}
                                    value={field.value}
                                >
                                    <IonSelectOption value="" style={{ fontStyle: "italic" }}><i>Kein Ausbilder</i></IonSelectOption>
                                    {allTrainers
                                        .filter((trainer) => trainer.id === watchTrainerId || !selectedTrainerIds.includes(trainer.id))
                                        .map((trainer) => (
                                            <IonSelectOption value={trainer.id} key={trainer.id}>
                                                {trainer.first_name} {trainer.name}
                                            </IonSelectOption>
                                        ))}
                                </IonSelect>}
                            />
                        }
                    </IonItem>

                    <p>Sie können noch weitere Ausbilder mit Ihrem Azubi verknüpfen. Diese sehen ebenfalls den aktuellen Status des Azubis und können Berichte akzeptieren / ablehnen.</p>
                    {fields.map((item, index) => {


                        return (
                            <div key={item.id} className="additionTrainersItem">
                                <IonItem className="registerInput">
                                    {(trainers && company_admins) &&
                                        <Controller
                                            name={`additional_trainers.${index}.trainerId`}
                                            control={control}
                                            render={({ field }) => <IonSelect
                                                label="Ausbilder"
                                                labelPlacement="floating"
                                                cancelText="Abbrechen"
                                                onIonChange={(e: { detail: { value: string | number | ChangeEvent<Element>; }; }) => field.onChange(e.detail.value)}
                                                value={field.value}
                                            >
                                                {allTrainers.map((trainer) => {
                                                    if ((trainer.id === watchAdditionalTrainers[index]?.trainerId) || !selectedTrainerIds.includes(trainer.id)) {
                                                        return (
                                                            <IonSelectOption value={trainer.id} key={trainer.id}>{trainer.first_name} {trainer.name}</IonSelectOption>
                                                        )
                                                    } return null
                                                })}
                                            </IonSelect>}
                                        />
                                    }
                                </IonItem>

                                <IonButton fill="clear" color="danger" onClick={() => remove(index)}>
                                    <IonIcon slot="icon-only" icon={removeCircleOutline}></IonIcon>
                                </IonButton>
                            </div>
                        )
                    })}
                    <IonButton fill="clear" disabled={selectedTrainerIds.length >= allTrainers.length || watchAdditionalTrainers.length >= allTrainers.length} onClick={() =>
                        append({ trainerId: undefined })
                    }>
                        <IonIcon slot="start" icon={addCircleOutline}></IonIcon>
                        weiterer Ausbilder
                    </IonButton>
                </form>
            </IonContent>

            <IonFooter>
                <IonToolbar>
                    <IonButtons slot="end" className="ion-padding">
                        <IonButton onClick={onCancel}>Abbrechen</IonButton>
                        <IonButton type="submit" onClick={handleSubmit(onSubmit)} fill="solid" color={"primary"}>Speichern</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonFooter>

        </IonModal >
    )
}

export default ConnectTraineeWithTrainerModal;